/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

import Input from '@components/atoms/ui-kit/input.tsx';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';

import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useAddUser } from '@utils/api/users/use-create-user.tsx';
import { USER_ROLES, UserRole } from '@utils/types/users/roles.ts';
import { BaseError } from '@utils/types/shared/base.ts';

export type CreateUserFormData = {
  first_name: string;
  last_name: string;
  email: string;
  roles: UserRole[];
};

type CreateUserFormProps = {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const FORM_ID = 'create-user-form';

const CreateUserForm = ({
  isModalOpen,
  setIsModalOpen
}: CreateUserFormProps) => {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();
  const isDark = theme === 'dark';
  const { mutateAsync: createUser } = useAddUser();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const schema = useMemo(
    () =>
      yup.object().shape({
        first_name: yup
          .string()
          .trim()
          .required(t('first-name-required'))
          .max(50, t('first-name-too-long')),
        last_name: yup
          .string()
          .trim()
          .required(t('last-name-required'))
          .max(50, t('last-name-too-long')),
        email: yup
          .string()
          .trim()
          .lowercase()
          .email(t('invalid-email'))
          .required(t('email-required'))
          .max(100, t('email-too-long')),
        roles: yup
          .array()
          .of(yup.string().oneOf(USER_ROLES).defined())
          .min(1, t('role-required'))
          .default([])
      }),
    [t]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<CreateUserFormData>({
    resolver: yupResolver(schema),
    defaultValues: { first_name: '', last_name: '', email: '', roles: [] },
    mode: 'onBlur'
  });

  useEffect(() => {
    if (isModalOpen) {
      reset();
      setIsSubmitting(false);
    }
  }, [isModalOpen, reset]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setIsModalOpen(false);
    }
  }, [isSubmitting, setIsModalOpen]);

  const onSubmit = useCallback(
    async (formData: CreateUserFormData) => {
      setIsSubmitting(true);
      try {
        await createUser(formData);
        toast.success(
          t(
            'user-added-successfully-the-user-will-be-sent-an-email-to-confirm-the-account'
          ),
          { duration: 20000 }
        );
        setIsModalOpen(false);
      } catch (error) {
        const fallbackMessage = t('failed-to-add-user');
        const data = (error as AxiosError<BaseError>)?.response?.data;

        let messages: string[] = [];
        if (Array.isArray(data?.detail)) {
          messages = data.detail.map((err: { loc?: string[]; msg: string }) => {
            const field = err.loc?.[1] ?? 'field';
            return `${field}: ${err.msg}`;
          });
        } else if (data?.message) {
          messages = [data.message];
        } else if (data?.error_description) {
          messages = [data.error_description];
        } else {
          messages = [fallbackMessage];
        }
        toast.error(messages.join('\n'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [createUser, setIsModalOpen, t]
  );

  const textColor = useMemo(() => getColour('normalText'), [getColour]);
  const helperTextColor = useMemo(() => getColour('inactiveText'), [getColour]);
  const requiredColor = isDark ? 'rgb(248,113,113)' : 'rgb(220,38,38)';
  const errorTextColor = requiredColor;

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={handleClose}
      size="small"
      title={t('create-user')}
      caption={t('create-user-caption')}
      footerActions={[
        <Button
          key="cancel"
          type="button"
          onClick={handleClose}
          label={t('cancel')}
          size="small"
          variant="smoke"
          disabled={isSubmitting}
        />,
        <Button
          key="create"
          type="submit"
          form={FORM_ID}
          label={isSubmitting ? t('creating') : t('create')}
          size="small"
          variant="primary"
          disabled={isSubmitting}
        />
      ]}
    >
      <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)} noValidate>
        <button type="submit" className="hidden" aria-hidden="true" />

        <div className="flex flex-col gap-5 w-full px-2">
          {/* Name Fields Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Controller
              name="first_name"
              control={control}
              render={({ field }) => (
                <Input
                  id="firstName"
                  label={t('first-name')}
                  placeholder={t('enter-first-name')}
                  type="text"
                  autoComplete="given-name"
                  autoFocus
                  required
                  {...field}
                  wrapperClass="w-full"
                  error={errors.first_name?.message}
                />
              )}
            />
            <Controller
              name="last_name"
              control={control}
              render={({ field }) => (
                <Input
                  id="lastName"
                  label={t('last-name')}
                  placeholder={t('enter-last-name')}
                  type="text"
                  autoComplete="family-name"
                  required
                  {...field}
                  wrapperClass="w-full"
                  error={errors.last_name?.message}
                />
              )}
            />
          </div>

          {/* Email Field */}
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <Input
                id="email"
                label={t('email')}
                placeholder={t('enter-email-address')}
                type="email"
                autoComplete="email"
                required
                {...field}
                wrapperClass="w-full"
                helperText={t('user-will-receive-confirmation-email')}
                error={errors.email?.message}
              />
            )}
          />

          {/* Roles Section */}
          <fieldset className="w-full">
            <legend
              id="roles-label"
              className="block text-sm font-semibold mb-3"
              style={{ color: textColor }}
            >
              {t('role')}
              <span
                className="ml-1"
                style={{ color: requiredColor }}
                aria-label="required"
              >
                *
              </span>
            </legend>

            <Controller
              name="roles"
              control={control}
              render={({ field: { value, onChange } }) => (
                <div
                  role="group"
                  aria-labelledby="roles-label"
                  aria-required="true"
                  aria-invalid={!!errors.roles}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                >
                  {USER_ROLES.map(role => {
                    const id = `role-${role}`;
                    const isChecked = value.includes(role as UserRole);
                    return (
                      <Checkbox
                        key={role}
                        id={id}
                        name="roles"
                        label={t(role)}
                        checked={isChecked}
                        variant="primary"
                        disabled={isSubmitting}
                        onChange={e => {
                          const next = e.target.checked
                            ? [...value, role as UserRole]
                            : value.filter(r => r !== role);
                          onChange(next);
                        }}
                      />
                    );
                  })}
                </div>
              )}
            />

            {errors.roles?.message && (
              <p
                className="mt-2 text-sm"
                style={{ color: errorTextColor }}
                role="alert"
              >
                {errors.roles.message}
              </p>
            )}

            {!errors.roles?.message && (
              <p className="mt-2 text-xs" style={{ color: helperTextColor }}>
                {t('select-at-least-one-role')}
              </p>
            )}
          </fieldset>
        </div>
      </form>
    </Modal>
  );
};

export default CreateUserForm;
